import { useSnackbar } from 'notistack';
import { useState, useEffect } from 'react';
import { ApolloQueryResult } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, FormProvider } from 'react-hook-form';

import { LoadingButton } from '@mui/lab';
import { Button, Dialog, Divider, DialogTitle, DialogActions, DialogContent } from '@mui/material';

import {
  LojaIntegradaStoreIntegrationType,
  useUpdateLojaIntegradaConfigMutation,
  UpdateLojaIntegradaConfigMutationVariables,
} from 'src/graphql/generated';

import { RHFTextField } from 'src/components/hook-form';

import {
  LojaIntegradaFormSchema,
  lojaIntegradaConfigFormSchemaDefaultValues,
} from '../schema/loja-integrada-form-schema';

type TAttributes = 'apiKey' | 'connected';

type TCurrentData = Pick<LojaIntegradaStoreIntegrationType, TAttributes>;

type Props = {
  open: boolean;
  onClose: () => void;
  disableForm?: boolean;
  currentData?: TCurrentData | null;
  refetch: () => Promise<ApolloQueryResult<any>>;
};

export function ConfigLojaIntegradaModal({
  open,
  onClose,
  disableForm,
  currentData,
  refetch,
}: Props) {
  const [enableForm, setEnableForm] = useState<boolean>(!disableForm);
  const [updateLojaIntegradaConfig, { loading }] = useUpdateLojaIntegradaConfigMutation();

  const { enqueueSnackbar } = useSnackbar();

  const methods = useForm({
    resolver: yupResolver(LojaIntegradaFormSchema),
    defaultValues: lojaIntegradaConfigFormSchemaDefaultValues,
  });

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = methods;

  const handleClose = () => {
    onClose();
    reset();
    setEnableForm(!disableForm);
  };

  const onSubmit = handleSubmit(async (data) => {
    const { apiKey } = data;

    const variables: UpdateLojaIntegradaConfigMutationVariables = {
      input: {
        apiKey,
      },
    };

    try {
      const { data: response } = await updateLojaIntegradaConfig({ variables });

      if (response?.saveLojaIntegradaStoreIntegration?.ok === true) {
        enqueueSnackbar('Cadastro efetuado com sucesso!', { variant: 'success' });
      } else {
        enqueueSnackbar('Erro ao salvar os dados da integração', { variant: 'error' });
      }

      setEnableForm(false);
      onClose();
      await refetch();
    } catch (error) {
      if (error?.message) {
        enqueueSnackbar(error?.message, { variant: 'error' });

        return;
      }
      enqueueSnackbar('Não foi possível atualizar os dados!', { variant: 'error' });
    }
  });

  useEffect(() => {
    const updateFields = () => {
      if (!currentData) return;

      const { apiKey } = currentData;

      reset({
        apiKey,
        connected: currentData.connected,
      });
    };

    updateFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentData]);

  useEffect(() => {
    setEnableForm(!disableForm);
  }, [disableForm]);

  return (
    <FormProvider {...methods}>
      <Dialog
        open={open}
        PaperProps={{
          sx: {
            minWidth: { xs: '90%', md: 600 },
          },
        }}
      >
        <DialogContent
          sx={{
            minWidth: { xs: 300, md: 600 },
            minHeight: 180,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 2,
            marginBottom: 2,
            py: 2,
          }}
        >
          <DialogTitle textAlign="center" fontSize={20}>
            Integração Loja Integrada
          </DialogTitle>

          <RHFTextField name="apiKey" label="Chave da API (API Key)" disabled={!enableForm} />
        </DialogContent>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={handleClose}>
            Fechar
          </Button>
          {enableForm && (
            <LoadingButton
              variant="contained"
              color="primary"
              type="submit"
              loading={isSubmitting || loading}
              onClick={onSubmit}
            >
              Salvar
            </LoadingButton>
          )}
          {!enableForm && (
            <Button variant="contained" color="primary" onClick={() => setEnableForm(true)}>
              {currentData ? 'Editar' : 'Criar'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
